import { useQuery } from '@apollo/client';
import { countryToRelavanceWord } from '@application/types';
import { useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type {
  AdoptionsLandingGetBreedersQuery,
  AdoptionsLandingGetBreedersQueryVariables,
} from '@gql/graphql';
import { CountryCode, DogBreederOrderByRelevanceFieldEnum, SortOrder } from '@gql/graphql';
import { useMemo } from 'react';

export const ADOPTIONS_LANDING_GET_BREEDERS = graphql(`
  query AdoptionsLandingGetBreeders(
    $where: DogBreederWhereInput
    $orderBy: [DogBreederOrderByWithRelationAndSearchRelevanceInput!]
    $take: Int
    $skip: Int
    $breederCardInfoTranslationsWhere: DogBreederTranslationWhereInput
    $breederCardInfoTranslationsWhereBreed: DogBreedTranslationWhereInput
    $breederCardInfoBreedImgsTake: Int
    $breederCardInfoLittersWhere: LitterWhereInput
  ) {
    findManyDogBreeder(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
      ...BreederCardInfo
    }
  }
`);

export const useGetBreeders = () => {
  const { language, countryCode } = useRouter();

  const variables = useMemo((): AdoptionsLandingGetBreedersQueryVariables => {
    return {
      take: 4,
      where: {
        AND: [{ certified: { equals: true } }, { public: { equals: true } }],
      },
      orderBy: [
        ...(countryCode !== CountryCode.EU
          ? [
              {
                _relevance: {
                  fields: [DogBreederOrderByRelevanceFieldEnum.tags],
                  search: countryToRelavanceWord[countryCode],
                  sort: SortOrder.desc,
                },
              },
            ]
          : []),
        { createdAt: SortOrder.desc },
      ],
      breederCardInfoTranslationsWhere: { language: { equals: language } },
      breederCardInfoTranslationsWhereBreed: { language: { equals: language } },
      breederCardInfoBreedImgsTake: 1,
      breederCardInfoLittersWhere: {
        archived: { equals: false },
        adminValidated: { equals: true },
      },
    };
  }, [countryCode, language]);

  const { data, error, loading } = useQuery<
    AdoptionsLandingGetBreedersQuery,
    AdoptionsLandingGetBreedersQueryVariables
  >(ADOPTIONS_LANDING_GET_BREEDERS, {
    variables,
  });

  return {
    breeders: data?.findManyDogBreeder ?? [],
    breedersError: error,
    breedersLoading: loading,
    breedersVariables: variables,
  };
};
